/**
 * Kit visual del panel admin (v1).
 * Tokens y piezas para replicar el look del directorio en otros módulos.
 */
import React from 'react';
import { isNarrowViewport } from '../../useMediaQuery';

/* Viewport angosto al cargar el kit (ver ajustes móviles al final de `studio`). */
const NARROW_KIT = isNarrowViewport();

export const ink = {
    text: '#1c1d21',
    muted: '#5f6368',
    faint: '#80868b',
    line: '#e8eaed',
    lineStrong: '#dadce0',
    wash: '#f4f5f7',
    surface: '#ffffff',
    accent: '#5E35B1',
    accentSoft: '#f4eefc',
    accentText: '#4527a0',
    danger: '#c5221f',
    dangerSoft: '#fce8e6',
};

export const studio = {
    page: { display: 'flex', flexDirection: 'column', gap: '20px' },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '24px',
        flexWrap: 'wrap',
        paddingBottom: '4px',
    },
    kicker: {
        margin: 0,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: ink.accent,
    },
    title: {
        margin: '6px 0 0',
        fontSize: '1.7rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.03em',
        lineHeight: 1.15,
    },
    subtitle: {
        margin: '8px 0 0',
        color: ink.muted,
        fontSize: '0.95rem',
        maxWidth: '540px',
        lineHeight: 1.5,
    },
    stats: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
    stat: {
        minWidth: '92px',
        padding: '12px 16px 11px',
        backgroundColor: ink.surface,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: ink.line,
        borderLeftWidth: '3px',
        borderLeftColor: ink.accent,
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    statValue: { fontSize: '1.35rem', fontWeight: 600, color: ink.text, letterSpacing: '-0.03em', lineHeight: 1.1 },
    statLabel: {
        fontSize: '0.7rem',
        color: ink.faint,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 600,
    },
    tabBar: {
        display: 'flex',
        gap: '4px',
        borderBottom: `1px solid ${ink.line}`,
        marginTop: '4px',
    },
    tab: {
        appearance: 'none',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
        marginBottom: '-1px',
        padding: '11px 16px 12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.95rem',
        color: ink.muted,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    tabActive: {
        color: ink.accentText,
        fontWeight: 600,
        borderBottomColor: ink.accent,
    },
    tabBadge: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: ink.muted,
        backgroundColor: ink.wash,
        borderRadius: '999px',
        padding: '1px 8px',
        minWidth: '20px',
        textAlign: 'center',
    },
    tabBadgeActive: { color: ink.accentText, backgroundColor: ink.accentSoft },
    hint: { margin: 0, color: ink.faint, fontSize: '0.84rem' },
    card: {
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: '16px',
        padding: '22px 24px',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '18px',
        minHeight: '40px',
    },
    dockActionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    primaryBtn: {
        padding: '10px 16px',
        backgroundColor: ink.accent,
        color: '#ffffff',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        fontFamily: 'inherit',
    },
    ghostBtn: {
        padding: '10px 16px',
        backgroundColor: 'transparent',
        color: ink.muted,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        fontFamily: 'inherit',
    },
    dangerBtn: {
        padding: '10px 16px',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        fontFamily: 'inherit',
    },
    control: {
        padding: '12px 14px',
        borderRadius: '14px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        fontSize: '0.95rem',
        backgroundColor: ink.surface,
        width: '100%',
        minHeight: '44px',
        fontFamily: 'inherit',
        color: ink.text,
        boxSizing: 'border-box',
    },
    controlCompact: {
        padding: '8px 12px',
        borderRadius: '14px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        fontSize: '0.88rem',
        backgroundColor: ink.surface,
        width: '100%',
        minHeight: '36px',
        fontFamily: 'inherit',
        color: ink.text,
        boxSizing: 'border-box',
    },
    filterBar: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '12px',
        marginBottom: '8px',
        padding: '14px',
        backgroundColor: ink.wash,
        borderRadius: '12px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
    },
    filterDock: {
        marginTop: 4,
        marginBottom: 8,
        padding: '16px 16px 14px',
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 16,
    },
    searchWrap: {
        position: 'relative',
        marginBottom: 12,
    },
    searchIcon: {
        position: 'absolute',
        left: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        pointerEvents: 'none',
    },
    searchInput: {
        width: '100%',
        boxSizing: 'border-box',
        height: 48,
        padding: '0 44px',
        borderRadius: 14,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        fontSize: '0.98rem',
        fontFamily: 'inherit',
        color: ink.text,
        outline: 'none',
    },
    clearSearch: {
        position: 'absolute',
        right: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 28,
        height: 28,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: ink.wash,
        color: ink.muted,
        cursor: 'pointer',
        fontSize: '1.1rem',
        lineHeight: 1,
        fontFamily: 'inherit',
    },
    filterPills: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    filterPill: {
        appearance: 'none',
        WebkitAppearance: 'none',
        padding: '9px 32px 9px 14px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        fontSize: '0.84rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        minWidth: 120,
        backgroundColor: ink.surface,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '12px',
    },
    filterMetaRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 12,
        minHeight: 28,
    },
    filterMetaText: {
        margin: 0,
        fontSize: '0.82rem',
        color: ink.faint,
        fontWeight: 600,
    },
    filterChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
    },
    filterChip: {
        appearance: 'none',
        border: 'none',
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        borderRadius: 999,
        padding: '5px 8px 5px 12px',
        fontSize: '0.78rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    filterChipX: {
        fontSize: '0.95rem',
        lineHeight: 1,
        opacity: 0.7,
    },
    filterClearAll: {
        appearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        color: ink.muted,
        fontSize: '0.78rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        padding: '5px 4px',
        textDecoration: 'underline',
        textUnderlineOffset: 3,
    },
    fieldLabel: {
        display: 'block',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.faint,
        marginBottom: '6px',
    },
    formPanel: {
        marginBottom: '20px',
        padding: '16px 16px 14px',
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: '16px',
    },
    formTitle: {
        margin: '0 0 16px',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
    },
    helper: {
        margin: '0',
        padding: '10px 12px',
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        borderRadius: '10px',
        fontSize: '0.85rem',
        lineHeight: 1.45,
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        tableLayout: 'auto',
    },
    th: {
        padding: '10px 12px',
        textAlign: 'left',
        backgroundColor: ink.wash,
        color: ink.faint,
        fontWeight: 700,
        fontSize: '0.72rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        borderBottom: `1px solid ${ink.line}`,
        whiteSpace: 'nowrap',
    },
    td: {
        padding: '12px',
        borderBottom: `1px solid ${ink.line}`,
        fontSize: '0.9rem',
        color: ink.text,
        verticalAlign: 'middle',
    },
    trHover: { backgroundColor: '#fafafa' },
    groupHead: {
        margin: '22px 0 8px',
        padding: '0 2px',
        fontSize: '0.8rem',
        fontWeight: 700,
        color: ink.accentText,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
    },
    iconBtn: {
        marginRight: '6px',
        padding: 0,
        width: '34px',
        height: '34px',
        backgroundColor: ink.surface,
        color: ink.accent,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: '9px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconBtnDanger: {
        marginRight: 0,
        padding: 0,
        width: '34px',
        height: '34px',
        backgroundColor: ink.surface,
        color: ink.danger,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: '9px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        objectFit: 'cover',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        display: 'block',
    },
    empty: {
        padding: '36px 16px',
        textAlign: 'center',
        color: ink.faint,
        fontSize: '0.95rem',
    },
    permCard: {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: '12px',
        padding: '12px 14px 8px',
        backgroundColor: ink.surface,
    },
    permCardTitle: {
        margin: 0,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.accentText,
    },
    permCardHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
        paddingBottom: 8,
        borderBottom: `1px solid ${ink.line}`,
    },
    permRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '9px 0',
        cursor: 'pointer',
    },
    permRowLabel: {
        fontSize: '0.88rem',
        color: ink.text,
        lineHeight: 1.35,
    },
    switchTrack: {
        appearance: 'none',
        border: 'none',
        width: 40,
        height: 22,
        borderRadius: 999,
        backgroundColor: ink.lineStrong,
        position: 'relative',
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
        fontFamily: 'inherit',
    },
    switchTrackOn: {
        backgroundColor: ink.accent,
    },
    switchThumb: {
        position: 'absolute',
        top: 2,
        left: 2,
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.16)',
        pointerEvents: 'none',
    },
    switchThumbOn: {
        left: 20,
    },
    split: {
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 260px) 1fr',
        gap: '0',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: '14px',
        overflow: 'hidden',
        minHeight: '480px',
        backgroundColor: ink.surface,
    },
    splitNav: {
        borderRight: `1px solid ${ink.line}`,
        backgroundColor: ink.wash,
        padding: '10px 8px',
        overflowY: 'auto',
        maxHeight: '70vh',
    },
    splitMain: {
        padding: '8px 0',
        overflowY: 'auto',
        maxHeight: '70vh',
        backgroundColor: ink.surface,
    },
    scopeBar: {
        display: 'inline-flex',
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: '10px',
        padding: '3px',
        gap: '2px',
    },
    scopeOn: {
        appearance: 'none',
        border: 'none',
        borderRadius: 8,
        padding: '7px 12px',
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        backgroundColor: ink.surface,
        color: ink.accentText,
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    },
    scopeOff: {
        appearance: 'none',
        border: 'none',
        borderRadius: 8,
        padding: '7px 12px',
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: ink.muted,
    },
    entityGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 12,
    },
    entityCard: {
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    entityHit: {
        appearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        textAlign: 'center',
        padding: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        width: '100%',
    },
    logoWrap: {
        width: '100%',
        height: 72,
        borderRadius: 12,
        backgroundColor: ink.wash,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    logo: {
        maxWidth: '80%',
        maxHeight: 56,
        objectFit: 'contain',
    },
    entityName: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.95rem',
        letterSpacing: '-0.02em',
    },
    entityMeta: {
        fontSize: '0.8rem',
        color: ink.faint,
    },
    entityActions: {
        padding: '10px 12px',
        borderTop: `1px solid ${ink.line}`,
        backgroundColor: ink.wash,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 4,
    },
    crumbBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 8,
        flexWrap: 'wrap',
    },
    crumbBtn: {
        appearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: ink.accentText,
    },
    crumbSep: { color: ink.faint, fontSize: '0.82rem' },
    crumbHere: { color: ink.muted, fontSize: '0.82rem', fontWeight: 600 },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
    },
};

/*
 * Ajustes móviles del kit. Se aplican una vez al cargar el módulo cuando el
 * viewport es angosto (≤ NARROW_MAX); los valores de escritorio no cambian.
 * Así todas las vistas que usan `studio.*` ganan paddings, áreas de toque y
 * tablas desplazables sin tocarlas una por una.
 */
if (NARROW_KIT) {
    const touch = { minHeight: 40 };
    Object.assign(studio.page, { gap: '14px' });
    Object.assign(studio.header, { gap: '14px', alignItems: 'flex-start' });
    Object.assign(studio.title, { fontSize: '1.35rem', overflowWrap: 'anywhere' });
    Object.assign(studio.subtitle, { fontSize: '0.9rem' });
    Object.assign(studio.stats, { flexWrap: 'nowrap', overflowX: 'auto', width: '100%', paddingBottom: '2px', scrollbarWidth: 'none' });
    Object.assign(studio.stat, { minWidth: '84px', padding: '10px 12px 9px', flexShrink: 0 });
    Object.assign(studio.statValue, { fontSize: '1.15rem' });
    Object.assign(studio.tabBar, { overflowX: 'auto', flexWrap: 'nowrap', scrollbarWidth: 'none' });
    Object.assign(studio.tab, { padding: '11px 12px 12px', whiteSpace: 'nowrap', flexShrink: 0 });
    Object.assign(studio.card, { padding: '14px 14px', borderRadius: '14px' });
    Object.assign(studio.toolbar, { flexWrap: 'wrap' });
    Object.assign(studio.dockActionRow, { flexWrap: 'wrap' });
    Object.assign(studio.primaryBtn, touch);
    Object.assign(studio.ghostBtn, touch);
    Object.assign(studio.dangerBtn, touch);
    Object.assign(studio.control, { fontSize: '16px' });
    Object.assign(studio.controlCompact, { fontSize: '16px', minHeight: '40px' });
    Object.assign(studio.filterBar, { gridTemplateColumns: 'minmax(0, 1fr)', padding: '12px' });
    Object.assign(studio.filterDock, { padding: '12px 12px 10px' });
    Object.assign(studio.searchInput, { fontSize: '16px', height: 46 });
    Object.assign(studio.clearSearch, { width: 34, height: 34 });
    Object.assign(studio.filterPill, { minWidth: 0, minHeight: 40, maxWidth: '100%' });
    Object.assign(studio.filterChip, { padding: '8px 10px 8px 12px' });
    Object.assign(studio.filterClearAll, { padding: '8px 4px' });
    Object.assign(studio.formPanel, { padding: '12px 12px 10px' });
    /* Tablas: en bloque con scroll horizontal propio, para no desbordar la página. */
    Object.assign(studio.table, { display: 'block', overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' });
    Object.assign(studio.th, { padding: '9px 10px' });
    Object.assign(studio.td, { padding: '10px' });
    Object.assign(studio.iconBtn, { width: '40px', height: '40px' });
    Object.assign(studio.iconBtnDanger, { width: '40px', height: '40px' });
    Object.assign(studio.empty, { padding: '24px 12px' });
    Object.assign(studio.permRow, { padding: '12px 0' });
    Object.assign(studio.split, { gridTemplateColumns: 'minmax(0, 1fr)', minHeight: 0 });
    Object.assign(studio.splitNav, { borderRight: 'none', borderBottom: `1px solid ${ink.line}`, maxHeight: '38vh' });
    Object.assign(studio.splitMain, { maxHeight: 'none' });
    Object.assign(studio.scopeOn, touch);
    Object.assign(studio.scopeOff, touch);
    Object.assign(studio.entityGrid, { gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 });
    Object.assign(studio.entityActions, { padding: '6px 8px' });
    Object.assign(studio.crumbBtn, { padding: '6px 0' });
    Object.assign(studio.formGrid, { gridTemplateColumns: 'minmax(0, 1fr)' });
}

const ROLE_TONE = {
    admin: { backgroundColor: ink.accentSoft, color: ink.accentText },
    pm: { backgroundColor: '#e8f0fe', color: '#174ea6' },
    colaborador: { backgroundColor: '#e6f4ea', color: '#137333' },
    cliente: { backgroundColor: ink.wash, color: ink.muted },
};

export function RoleBadge({ roleId, label }) {
    const tone = ROLE_TONE[roleId] || ROLE_TONE.cliente;
    return (
        <span style={{
            display: 'inline-block',
            padding: '3px 9px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.01em',
            ...tone,
        }}>
            {label || roleId}
        </span>
    );
}

export function StudioTabs({ items, value, onChange }) {
    return (
        <div style={studio.tabBar} role="tablist">
            {items.map((item) => {
                const selected = item.id === value;
                return (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => onChange(item.id)}
                        style={{ ...studio.tab, ...(selected ? studio.tabActive : {}) }}
                    >
                        {item.label}
                        {item.count != null && (
                            <span style={{ ...studio.tabBadge, ...(selected ? studio.tabBadgeActive : {}) }}>
                                {item.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Plantilla de columnas responsiva: en pantallas angostas siempre una columna.
 * @param {string} desktop Valor de `gridTemplateColumns` en escritorio.
 * @returns {string}
 */
export const cols = (desktop) => (NARROW_KIT ? 'minmax(0, 1fr)' : desktop);

/** `gridColumn` que ocupa la fila completa con cualquier número de columnas. */
export const FULL_ROW = '1 / -1';

export function StudioIcon({ src, alt }) {
    return <img src={src} alt={alt} style={{ width: 16, height: 16, display: 'block' }} />;
}

export function CatalogCard({ name, meta, logoUrl, onOpen, actions }) {
    const src = logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || '?')}&background=f4eefc&color=4527a0&size=128`;
    const body = (
        <>
            <div style={studio.logoWrap}>
                <img src={src} alt="" style={studio.logo} />
            </div>
            <div style={studio.entityName}>{name}</div>
            {meta != null && <div style={studio.entityMeta}>{meta}</div>}
        </>
    );
    return (
        <div style={studio.entityCard}>
            {onOpen ? (
                <button type="button" onClick={onOpen} style={studio.entityHit}>{body}</button>
            ) : (
                <div style={{ ...studio.entityHit, cursor: 'default' }}>{body}</div>
            )}
            {actions ? <div style={studio.entityActions}>{actions}</div> : null}
        </div>
    );
}

const PILL_CHEVRON = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#80868b" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>')}")`;

export function pillTone(active) {
    return {
        borderColor: active ? ink.accent : ink.lineStrong,
        backgroundColor: active ? ink.accentSoft : ink.surface,
        color: active ? ink.accentText : ink.text,
        fontWeight: active ? 600 : 500,
    };
}

export function Field({ label, span, children }) {
    return (
        <label style={span && !NARROW_KIT ? { gridColumn: `span ${span}` } : undefined}>
            {label ? <span style={studio.fieldLabel}>{label}</span> : null}
            {children}
        </label>
    );
}

export function SearchField({ value, onChange, placeholder, style }) {
    return (
        <div style={{ ...studio.searchWrap, marginBottom: style?.marginBottom ?? studio.searchWrap.marginBottom, ...style }}>
            <span style={studio.searchIcon} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ink.faint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.2-3.2" />
                </svg>
            </span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                style={studio.searchInput}
            />
            {value ? (
                <button type="button" onClick={() => onChange('')} style={studio.clearSearch} title="Limpiar búsqueda">×</button>
            ) : null}
        </div>
    );
}

export function FilterDock({ children, style, variant }) {
    const raised = variant === 'card'
        ? {
            backgroundColor: '#ffffff',
            borderColor: '#c4c7cc',
            boxShadow: '0 1px 3px rgba(28, 29, 33, 0.10)',
        }
        : {};
    return <div style={{ ...studio.filterDock, ...raised, ...style }}>{children}</div>;
}

export function DockActionRow({ children, action }) {
    return (
        <div style={studio.dockActionRow}>
            <div style={{ flex: 1, minWidth: NARROW_KIT ? '100%' : 0 }}>{children}</div>
            {action || null}
        </div>
    );
}

export function PermSwitch({ checked, onChange, label, disabled }) {
    return (
        <label style={{ ...studio.permRow, ...(disabled ? { opacity: 0.5, cursor: 'default' } : {}) }}>
            <span style={studio.permRowLabel}>{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={!!checked}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                style={{ ...studio.switchTrack, ...(checked ? studio.switchTrackOn : {}) }}
            >
                <span style={{ ...studio.switchThumb, ...(checked ? studio.switchThumbOn : {}) }} />
            </button>
        </label>
    );
}

export function PermGroup({ title, count, total, onToggleAll, children }) {
    return (
        <div style={studio.permCard}>
            <div style={studio.permCardHead}>
                <h5 style={studio.permCardTitle}>{title}</h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={studio.tabBadge}>{count}/{total}</span>
                    {onToggleAll && (
                        <button type="button" onClick={onToggleAll} style={studio.filterClearAll}>
                            {count === total ? 'Ninguno' : 'Todos'}
                        </button>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}

export function FilterPill({ value, onChange, disabled, emptyLabel, children, style, name }) {
    return (
        <select
            name={name}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value, e)}
            style={{
                ...studio.filterPill,
                backgroundImage: PILL_CHEVRON,
                ...pillTone(!!value),
                opacity: disabled ? 0.55 : 1,
                ...style,
            }}
        >
            <option value="">{emptyLabel}</option>
            {children}
        </select>
    );
}

export function FilterDate({ value, onChange, title, name }) {
    return (
        <input
            type="date"
            name={name}
            title={title}
            value={value}
            onChange={(e) => onChange(e.target.value, e)}
            style={{
                ...studio.filterPill,
                paddingRight: 14,
                minWidth: 150,
                ...pillTone(!!value),
            }}
        />
    );
}

export function FilterMeta({ summary, chips = [], onClearAll }) {
    if (!summary && !chips.length) return null;
    return (
        <div style={studio.filterMetaRow}>
            {summary ? <p style={studio.filterMetaText}>{summary}</p> : <span />}
            {chips.length > 0 && (
                <div style={studio.filterChips}>
                    {chips.map((item) => (
                        <button key={item.key} type="button" onClick={item.onClear} style={studio.filterChip} title="Quitar filtro">
                            {item.label}
                            <span style={studio.filterChipX}>×</span>
                        </button>
                    ))}
                    {onClearAll && (
                        <button type="button" onClick={onClearAll} style={studio.filterClearAll}>Limpiar todo</button>
                    )}
                </div>
            )}
        </div>
    );
}

export function ProcessingOverlay({ message = 'Procesando…' }) {
    return (
        <div
            role="alertdialog"
            aria-busy="true"
            aria-live="assertive"
            aria-label={message}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 40000,
                backgroundColor: 'rgba(28, 29, 33, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
            }}
        >
            <style>{'@keyframes cmProcessSpin { to { transform: rotate(360deg); } }'}</style>
            <div
                style={{
                    backgroundColor: ink.surface,
                    borderRadius: 16,
                    padding: '28px 32px',
                    minWidth: 240,
                    maxWidth: 360,
                    textAlign: 'center',
                    boxShadow: '0 18px 40px rgba(28, 29, 33, 0.18)',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: ink.line,
                }}
            >
                <div
                    style={{
                        width: 36,
                        height: 36,
                        margin: '0 auto 16px',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        borderColor: ink.accentSoft,
                        borderTopColor: ink.accent,
                        borderRadius: '50%',
                        animation: 'cmProcessSpin 0.7s linear infinite',
                    }}
                />
                <p style={{ margin: 0, fontWeight: 700, color: ink.text, fontSize: '1.05rem' }}>Procesando</p>
                <p style={{ margin: '8px 0 0', color: ink.muted, fontSize: '0.9rem', lineHeight: 1.4 }}>{message}</p>
            </div>
        </div>
    );
}
